import { ChannelIdSchema, PartySlugSchema, isReservedPartySlug } from '@mixwire/contracts';
import { useEffect, type ReactElement } from 'react';

import { SettingsMenu } from './components/SettingsMenu.js';
import { DjPage } from './pages/DjPage.js';
import { ExportPage } from './pages/ExportPage.js';
import { JoinPage } from './pages/JoinPage.js';
import { PartyViewPage } from './pages/PartyViewPage.js';
import { SuggesterGate } from './pages/SuggesterGate.js';
import { WrappedPage } from './pages/WrappedPage.js';
import { canonicalPath } from './routing/canonical.js';
import { matchRoute } from './routing/match.js';
import { useRoute } from './routing/useRoute.js';

import type { PartySlug } from '@mixwire/contracts';

const partySlugOfSegment = (segment: string | undefined): PartySlug | undefined => {
  if (segment === undefined || isReservedPartySlug(segment)) {
    return undefined;
  }
  const parsed = PartySlugSchema.safeParse(segment);
  return parsed.success ? parsed.data : undefined;
};

const partySlugForRoute = (pattern: string, path: string): PartySlug | undefined => {
  const match = matchRoute(pattern, path);
  return match === undefined ? undefined : partySlugOfSegment(match['slug']);
};

const pageFor = (path: string): ReactElement => {
  const djMatch = matchRoute('/:slug/dj/:channelId', path);
  if (djMatch !== undefined) {
    const slug = partySlugOfSegment(djMatch['slug']);
    const channelId = ChannelIdSchema.safeParse(Number(djMatch['channelId']));
    if (slug !== undefined && channelId.success) {
      return <DjPage slug={slug} channelId={channelId.data} />;
    }
  }

  const suggestSlug = partySlugForRoute('/:slug/suggest', path);
  if (suggestSlug !== undefined) {
    return <SuggesterGate slug={suggestSlug} />;
  }

  const passcodePromptSlug = partySlugForRoute('/:slug/add', path);
  if (passcodePromptSlug !== undefined) {
    return <PartyViewPage slug={passcodePromptSlug} opensWithPasscodePrompt={true} />;
  }

  const wrappedSlug = partySlugForRoute('/:slug/wrapped', path);
  if (wrappedSlug !== undefined) {
    return <WrappedPage slug={wrappedSlug} />;
  }

  const exportSlug = partySlugForRoute('/:slug/export', path);
  if (exportSlug !== undefined) {
    return <ExportPage slug={exportSlug} />;
  }

  const partySlug = partySlugForRoute('/:slug', path);
  if (partySlug !== undefined) {
    return <PartyViewPage slug={partySlug} />;
  }

  return <JoinPage />;
};

export const App = (): ReactElement => {
  const { path } = useRoute();
  const canonical = canonicalPath(path);

  useEffect(() => {
    if (canonical !== path) {
      history.replaceState({}, '', `${canonical}${location.search}${location.hash}`);
    }
  }, [canonical, path]);

  return (
    <div className="min-h-dvh">
      <main className="mx-auto flex max-w-wide flex-col gap-4 px-6 py-6">{pageFor(canonical)}</main>

      <SettingsMenu />
    </div>
  );
};
